import {
  getFolderSnapshot,
  isFolderListViewedThisSession,
  isFolderSnapshotWarm,
} from "@/lib/mail/inboxFolderBus";
import { navFolderSlot } from "@/lib/mail/folderNavSections";
import type { MailFolder } from "@/lib/types/mail";

/** Compte les non lus dans le snapshot en-têtes (aligné sur la liste affichée). */
export function countUnreadInFolderSnapshot(accountId: number, folder: string): number {
  const snap = getFolderSnapshot(accountId, folder);
  if (!snap?.messages.length) return 0;
  return snap.messages.filter((m) => m.uid > 0 && !m.seen).length;
}

/**
 * Snapshot autoritaire : liste réelle (y compris vidée par delete), pas un
 * hydrate session / fenêtre incomplète. Dans ce cas on fait confiance au local
 * même si STATUS IMAP est encore en retard (sinon pastille 8 → 0 → 8 au switch).
 *
 * Liste déjà ouverte (y compris après F5 / nouvel onglet) : les drapeaux
 * lus locaux gagnent aussi (sinon fermer le compte → fantôme IMAP 0 → N → 0).
 */
function isAuthoritativeFolderSnapshot(
  accountId: number,
  folderName: string,
  snap: NonNullable<ReturnType<typeof getFolderSnapshot>>
): boolean {
  if (!isFolderSnapshotWarm(snap)) return false;
  if (snap.bootstrapped === true && snap.total === 0) return true;
  if (snap.fromCache !== true) return true;
  return isFolderListViewedThisSession(accountId, folderName);
}

/**
 * Pastille unread = non-lus du **snapshot liste** dès qu’il est hydraté.
 * Évite le fantôme STATUS IMAP (`folders.php`) > liste réelle.
 * Sans snapshot : on garde le compteur IMAP.
 *
 * Ne jamais faire descendre sous l’IMAP si le snapshot est session/`fromCache`
 * et que le dossier n’a pas encore été ouvert — sinon 1er switch de compte →
 * pastille 0 jusqu’au remount.
 * Exception : liste bootstrappée devenue vide (select-all delete) — le 0 est vrai.
 * Exception : dossier déjà ouvert dans le navigateur — le 0 local est vrai (IMAP en retard).
 */
export function mergeFoldersWithSnapshotUnread(
  accountId: number,
  folders: MailFolder[]
): MailFolder[] {
  if (folders.length === 0) return folders;

  return folders.map((folder) => {
    const snap = getFolderSnapshot(accountId, folder.name);
    const imap = folder.unread ?? 0;
    const hasLocalList =
      snap != null && (snap.bootstrapped === true || snap.messages.length > 0);

    if (!hasLocalList || !snap) {
      return folder;
    }

    const local = countUnreadInFolderSnapshot(accountId, folder.name);
    if (local === imap) return folder;

    // Snapshot froid / session incomplet, dossier jamais ouvert : garder IMAP.
    if (local < imap && !isAuthoritativeFolderSnapshot(accountId, folder.name, snap)) {
      return folder;
    }

    return { ...folder, unread: local };
  });
}

/** Après `folders.php?badges=1` : forcer unread = snapshot si la liste est hydratée. */
export function alignFoldersUnreadWithSnapshot(
  accountId: number,
  folders: MailFolder[]
): MailFolder[] {
  return mergeFoldersWithSnapshotUnread(accountId, folders);
}

export function sumInboxUnreadFromFolders(folders: MailFolder[]): number {
  let total = 0;
  for (const folder of folders) {
    if (navFolderSlot(folder.name, folder.label) === "inbox") {
      total += folder.unread ?? 0;
    }
  }
  return total;
}
